import { useMemo } from 'react'

import { Home02, Settings02, TrendUp01 } from '@foxino/icons-react'

import EnglishFlagFlatIcon from '@app/assets/flagIcons/flat/englishFlag.svg?react'
import FranceFlagFlatIcon from '@app/assets/flagIcons/flat/franceFlag.svg?react'
import GermanFlagFlatIcon from '@app/assets/flagIcons/flat/germanFlag.svg?react'
import SpanishFlagFlatIcon from '@app/assets/flagIcons/flat/spanishFlag.svg?react'
import type { RouteType } from '@app/config/router/navMenu.types'
import { Pages } from '@app/config/router/PagesEnum'
import { Language } from '@app/data'
import { useTestId } from '@app/hooks/useTestId'
import { useTranslation } from '@app/locales'
import { TutorialElementIds } from '@app/modules/floppyTutor/model/types'

import { useClassrooms } from './useClassrooms'

export const useNavigationPages = () => {
  const { t } = useTranslation(['common', 'classroom'])

  const testId = useTestId(['side_navigation'])

  const { classrooms } = useClassrooms()

  const navMenu: RouteType[] = useMemo(
    () => [
      {
        to: Pages.DISCOVERY(Language.ENGLISH.toLowerCase()),
        label: t('languages.english'),
        testId: testId('languages.english'),
        Icon: EnglishFlagFlatIcon,
        iconPosition: 'right',
        position: 'middle'
      },
      {
        to: Pages.DISCOVERY(Language.GERMAN.toLowerCase()),
        label: t('languages.german'),
        testId: testId('languages.german'),
        Icon: GermanFlagFlatIcon,
        iconPosition: 'right',
        position: 'middle'
      },
      {
        to: Pages.DISCOVERY(Language.FRENCH.toLowerCase()),
        label: t('languages.french'),
        testId: testId('languages.french'),

        Icon: FranceFlagFlatIcon,
        iconPosition: 'right',
        position: 'middle'
      },
      {
        to: Pages.DISCOVERY(Language.SPANISH.toLowerCase()),
        label: t('languages.spanish'),
        testId: testId('languages.spanish'),
        Icon: SpanishFlagFlatIcon,
        iconPosition: 'right',
        position: 'middle'
      }
    ],
    []
  )

  const topRoutes = useMemo(() => navMenu.filter((route) => route.position === 'top'), [])
  const middleRoutes = useMemo(() => navMenu.filter((route) => route.position === 'middle'), [])

  const classroomRoutes: RouteType[] = useMemo(
    () =>
      classrooms?.map((classroom) => {
        const isSetupComplete = classroom.isSetupComplete && !classroom.isReadyToMigrate

        return {
          color: classroom.color || ('default' as const),
          label: classroom.name,
          identificator: classroom.identificator,
          to: Pages.CLASSROOM_DETAIL(classroom.id),
          testId: testId('classroom.home'),
          subject: classroom.showSubjectIcon ? classroom.subject : undefined,
          children: !isSetupComplete
            ? []
            : [
                {
                  label: t('routes.learningPlan'),
                  to: Pages.CLASSROOM_LEARNING_PLAN(classroom.id),
                  testId: testId('classroom.learning_plan'),
                  Icon: Home02
                },
                {
                  label: t('routes.performance'),
                  to: Pages.CLASSROOM_PERFORMANCE(classroom.id),
                  testId: testId('classroom.performance'),
                  Icon: TrendUp01
                },
                {
                  id: TutorialElementIds.TUTOR_STEP_2,
                  label: t('routes.classSettings'),
                  testId: testId('classroom.settings'),
                  to: Pages.CLASSROOM_MANAGE_CLASS(classroom.id),
                  Icon: Settings02
                }
              ]
        }
      }) || [],
    [classrooms]
  )

  return {
    topRoutes,
    middleRoutes,
    classroomRoutes
  }
}
